import React, { useState } from "react";
import {
  IconTrash,
  IconPlus,
  IconSearch,
  IconReceiptTax,
  IconTrendingDown,
  IconTrendingUp,
  IconCheck,
  IconFileCertificate,
  IconAlertCircle
} from "@tabler/icons-react";
import { useDisposals, useAssets, disposeAsset } from "../../controllers/assets.controller";
import toast from "react-hot-toast";

export default function AssetDisposalTab() {
  const [search, setSearch] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data: disposals, error, isLoading, mutate } = useDisposals();
  const { data: assets } = useAssets();

  const [formData, setFormData] = useState({
    asset_id: "",
    disposal_type: "Sale",
    disposal_date: new Date().toISOString().split("T")[0],
    sale_price: "0",
    disposal_cost: "0",
    buyer_name: "",
    approved_by: "General Manager",
    reason: "Asset reached end of useful operational life",
    notes: ""
  });

  const [selectedAssetInfo, setSelectedAssetInfo] = useState(null);

  const filteredDisposals = Array.isArray(disposals)
    ? disposals.filter((d) => {
        if (!search) return true;
        const q = search.toLowerCase();
        return (
          d.asset_code?.toLowerCase().includes(q) ||
          d.asset_name?.toLowerCase().includes(q) ||
          d.disposal_type?.toLowerCase().includes(q) ||
          d.buyer_name?.toLowerCase().includes(q)
        );
      })
    : [];

  const handleOpenModal = () => {
    const firstEligible = assets?.find((a) => a.status !== "Disposed" && a.status !== "Written Off");
    setSelectedAssetInfo(firstEligible || null);
    setFormData({
      asset_id: firstEligible?.id || "",
      disposal_type: "Sale",
      disposal_date: new Date().toISOString().split("T")[0],
      sale_price: "0",
      disposal_cost: "0",
      buyer_name: "",
      approved_by: "General Manager",
      reason: "Asset replaced with newer energy-efficient equipment",
      notes: ""
    });
    setIsModalOpen(true);
  };

  const handleAssetSelect = (assetId) => {
    const asset = assets?.find((a) => String(a.id) === String(assetId));
    setSelectedAssetInfo(asset || null);
    setFormData((prev) => ({ ...prev, asset_id: assetId }));
  };

  const currentNBV = Number(selectedAssetInfo?.net_book_value ?? selectedAssetInfo?.cost ?? 0);
  const estimatedProceeds = Number(formData.sale_price || 0);
  const estimatedCost = Number(formData.disposal_cost || 0);
  const projectedGainLoss = estimatedProceeds - currentNBV - estimatedCost;

  const handleSubmitDisposal = async (e) => {
    e.preventDefault();
    if (!formData.asset_id) {
      toast.error("Please select an asset to dispose");
      return;
    }

    if (
      !window.confirm(
        `Are you sure you want to permanently dispose asset "${selectedAssetInfo?.asset_name}"? This will write-off its book value and generate accounting entries.`
      )
    ) {
      return;
    }

    try {
      await disposeAsset(formData);
      toast.success("Asset disposed and written off with GL entries!");
      setIsModalOpen(false);
      mutate();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to dispose asset");
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white dark:bg-gray-800 p-5 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <IconTrash className="w-6 h-6 text-restro-green" />
            Asset Disposal & Write-off Register
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Retire assets via sale, scrapping, donation, or write-off with automated Gain/Loss on Disposal calculation.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <IconSearch className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search disposed assets..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-4 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-restro-green w-64 text-gray-800 dark:text-gray-200"
            />
          </div>
          <button
            onClick={handleOpenModal}
            className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-rose-600 hover:bg-rose-700 rounded-xl shadow-sm transition"
          >
            <IconPlus className="w-4 h-4" />
            Dispose / Retire Asset
          </button>
        </div>
      </div>

      {/* Disposals Table */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-gray-600 dark:text-gray-300">
            <thead className="bg-gray-50 dark:bg-gray-700/50 text-gray-700 dark:text-gray-200 uppercase text-xs font-semibold">
              <tr>
                <th className="px-6 py-4">Asset Details</th>
                <th className="px-6 py-4">Method</th>
                <th className="px-6 py-4">Disposal Date</th>
                <th className="px-6 py-4">Net Book Value</th>
                <th className="px-6 py-4">Sale Proceeds</th>
                <th className="px-6 py-4">Gain / (Loss)</th>
                <th className="px-6 py-4">Authorized By</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {isLoading ? (
                <tr>
                  <td colSpan="7" className="text-center py-10 text-gray-400">
                    Loading disposal records...
                  </td>
                </tr>
              ) : filteredDisposals.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-12 text-gray-400">
                    <IconTrash className="w-12 h-12 mx-auto mb-2 opacity-30 text-rose-500" />
                    No assets retired or written off yet.
                  </td>
                </tr>
              ) : (
                filteredDisposals.map((d) => {
                  const gainLoss = Number(d.gain_or_loss || d.gain_loss || 0);
                  const isGain = gainLoss >= 0;
                  return (
                    <tr key={d.id} className="hover:bg-gray-50/50 dark:hover:bg-gray-750 transition">
                      <td className="px-6 py-4">
                        <div className="font-semibold text-gray-900 dark:text-white">
                          {d.asset_name || "Asset #" + d.asset_id}
                        </div>
                        <div className="text-xs text-gray-400 font-mono">
                          {d.asset_code || `AST-${d.asset_id}`}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex px-2.5 py-0.5 rounded text-xs font-semibold bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                          {d.disposal_type || "Sale"}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-xs">
                        {d.disposal_date ? new Date(d.disposal_date).toLocaleDateString() : "-"}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">
                        Rs. {Number(d.net_book_value || 0).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">
                        Rs. {Number(d.sale_price || d.proceeds || 0).toLocaleString()}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center gap-1 font-bold text-sm ${
                            isGain ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"
                          }`}
                        >
                          {isGain ? <IconTrendingUp className="w-4 h-4" /> : <IconTrendingDown className="w-4 h-4" />}
                          Rs. {Math.abs(gainLoss).toLocaleString()} {isGain ? "(Gain)" : "(Loss)"}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-xs text-gray-700 dark:text-gray-300">
                        {d.approved_by || "Management"}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal: Dispose / Retire Asset */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-xl overflow-hidden border border-gray-100 dark:border-gray-700 my-8">
            <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <IconTrash className="w-5 h-5 text-rose-500" />
                Dispose / Write-off Asset
              </h3>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleSubmitDisposal} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Select Asset to Retire *
                </label>
                <select
                  value={formData.asset_id}
                  onChange={(e) => handleAssetSelect(e.target.value)}
                  className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200"
                  required
                >
                  <option value="">-- Choose asset --</option>
                  {assets
                    ?.filter((a) => a.status !== "Disposed")
                    .map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.asset_code} - {a.asset_name} (NBV: Rs. {Number(a.net_book_value || a.cost || 0).toLocaleString()})
                      </option>
                    ))}
                </select>
              </div>

              {selectedAssetInfo && (
                <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded-xl border border-gray-200 dark:border-gray-700 grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <span className="text-gray-400">Original Cost:</span>{" "}
                    <span className="font-semibold text-gray-800 dark:text-gray-200">
                      Rs. {Number(selectedAssetInfo.purchase_cost || selectedAssetInfo.cost || 0).toLocaleString()}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-400">Net Book Value:</span>{" "}
                    <span className="font-bold text-gray-900 dark:text-white">
                      Rs. {currentNBV.toLocaleString()}
                    </span>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    Disposal Method *
                  </label>
                  <select
                    value={formData.disposal_type}
                    onChange={(e) => setFormData({ ...formData, disposal_type: e.target.value })}
                    className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200 font-semibold"
                  >
                    <option value="Sale">Sale / Liquidation</option>
                    <option value="Scrap">Scrap / Salvage Value</option>
                    <option value="Write-off">Complete Write-off (Zero Salvage)</option>
                    <option value="Trade-in">Trade-in Exchange</option>
                    <option value="Donation">Charitable Donation</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    Disposal Date *
                  </label>
                  <input
                    type="date"
                    value={formData.disposal_date}
                    onChange={(e) => setFormData({ ...formData, disposal_date: e.target.value })}
                    className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    Sale Proceeds / Salvage Recovered (NPR)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.sale_price}
                    onChange={(e) => setFormData({ ...formData, sale_price: e.target.value })}
                    className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    Removal / Disposal Costs (NPR)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.disposal_cost}
                    onChange={(e) => setFormData({ ...formData, disposal_cost: e.target.value })}
                    className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200"
                  />
                </div>
              </div>

              {/* Calculated Gain/Loss Simulation Banner */}
              <div
                className={`p-3 rounded-xl border flex items-center justify-between text-xs font-semibold ${
                  projectedGainLoss >= 0
                    ? "bg-emerald-50 dark:bg-emerald-950/40 border-emerald-200 dark:border-emerald-800 text-emerald-800 dark:text-emerald-300"
                    : "bg-rose-50 dark:bg-rose-950/40 border-rose-200 dark:border-rose-800 text-rose-800 dark:text-rose-300"
                }`}
              >
                <span>Calculated Accounting Impact:</span>
                <span className="font-bold text-sm">
                  {projectedGainLoss >= 0 ? "Gain on Disposal" : "Loss on Disposal"}: Rs.{" "}
                  {Math.abs(projectedGainLoss).toLocaleString()}
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    Buyer / Scrap Dealer / Recipient
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Kathmandu Scrap Recyclers"
                    value={formData.buyer_name}
                    onChange={(e) => setFormData({ ...formData, buyer_name: e.target.value })}
                    className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    Approved By *
                  </label>
                  <input
                    type="text"
                    value={formData.approved_by}
                    onChange={(e) => setFormData({ ...formData, approved_by: e.target.value })}
                    className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Reason for Disposal & Certificate Notes
                </label>
                <textarea
                  rows="2"
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-xl transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 text-sm font-semibold text-white bg-rose-600 hover:bg-rose-700 rounded-xl shadow-sm transition"
                >
                  Confirm Disposal & Post to GL
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
